import {
  Accordion,
  Box,
  Button,
  Checkbox,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useRouter } from "@uirouter/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../core/auth";
import useApi from "../../../shared/hooks/useApi";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import {
  formatErrorResponse,
  isServerErrorCode,
} from "../../../shared/utils/format-response-error";
import SignatureCanvas from "../../servicePlan/components/SignatureCanvas";
import CollapsibleSection from "./CollapsibleSection";
import CopyFindingsPopover from "./CopyFindingsPopover";
import { Disposition, DISPOSITIONS } from "./dispositions";
import DailyNoteDetailsDrawer from "./DailyNoteDetailsDrawer";
import { INCIDENT_SUBTYPES, IncidentSubtype } from "./incidentSubtypes";

// The row type the dashboard hands to this modal — see DailyNotesRoute.
import type { ListItem } from "../DailyNotesRoute";

/**
 * One summary figure, boxed and tinted.
 *
 * The accents are the dashboard's own priority colours (`PRIORITY_STYLE` in
 * DailyNotesRoute), so red and orange carry the same meaning here as they do on the
 * list behind this modal. The note total takes grey deliberately: it is a count, not
 * a priority, and giving it a priority colour would imply one.
 */
export function SummaryBox(props: {
  label: string;
  value: number;
  accent: "gray" | "red" | "orange";
  /** Drawn before the figure. For the one tile that is a consequence, not a tally. */
  icon?: React.ReactNode;
}) {
  return (
    // No `flex={1}`: stretching each tile to an equal share of the row left most of
    // their width empty, with the figure stranded in the corner. Sized to its content,
    // a tile is as wide as what it says.
    //
    // The number and its label sit on one line for the same reason -- stacked, the
    // label defined the width and the number sat alone above it.
    <Flex
      align="center"
      bg={`${props.accent}.50`}
      borderColor={`${props.accent}.200`}
      borderRadius="md"
      borderWidth={1}
      gap={3}
      px={5}
      py={3}
    >
      {props.icon}
      <Text color={`${props.accent}.800`} fontSize="3xl" fontWeight="bold" lineHeight={1}>
        {props.value}
      </Text>
      <Text color={`${props.accent}.700`} fontSize="md" fontWeight="medium">
        {props.label}
      </Text>
    </Flex>
  );
}

/** The longest findings text the server accepts for one note. */
const FINDINGS_MAX_LENGTH = 4000;

/**
 * The signing step: the manifest of notes being signed, the declaration, and the
 * identity-and-intent confirmation that gates the button.
 *
 * The declaration text and its version both come from the server. The version goes
 * back with the signature so a reviewer who left this open across a wording change
 * is refused rather than signing text they never read.
 */
export default function DailyNoteAttestationModal(props: {
  isOpen: boolean;
  notes: ListItem[];
  onClose: () => void;
  onSigned: () => void;
}) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { stateService } = useRouter();

  const [confirmed, setConfirmed] = React.useState(false);

  // The nurse's ruling per flagged note, keyed by note id.
  //
  // Held here rather than derived from the rows because it is the reviewer's answer,
  // not a property of the note. The server refuses a batch that leaves any HIGH or
  // URGENT note unanswered, so this gates the button too.
  const [determinations, setDeterminations] = React.useState<
    Record<string, "ROUTINE" | "NON_ROUTINE">
  >({});

  // What was done about each note the reviewer raises an incident for. Several can apply, and
  // at least one is required; a note without an incident is not asked.
  const [dispositions, setDispositions] = React.useState<Record<string, Disposition[]>>({});

  // Whether the reviewer chose to open a Salesforce incident, per note. An incident is never
  // raised on its own: only a concerning ruling offers the choice, and only an explicit tick
  // counts (see `incidentFor`).
  const [createIncidents, setCreateIncidents] = React.useState<Record<string, boolean>>({});

  // The incident subtype the reviewer picked per note, keyed by note id.
  //
  // Only an entry the reviewer made lives here. A note she has not touched falls back to the
  // classifier's suggestion (see `subtypeFor`), which is what makes this "confirm or change"
  // and not "start from nothing" -- and keeping the two apart means a suggestion is never
  // mistaken for a choice when deciding what was entered.
  const [subtypes, setSubtypes] = React.useState<Record<string, IncidentSubtype | null>>({});

  // The nurse's "Findings, Evidence, and Recommendations" per note, keyed by note id. Printed on
  // that note's PDF between the caregiver's note and the signature; optional, and blank means
  // none.
  const [findings, setFindings] = React.useState<Record<string, string>>({});

  // Copies are independent: editing one note afterwards changes only that note.
  const copyFindings = (text: string, targetIds: string[]) => {
    setFindings((prev) => ({
      ...prev,
      ...Object.fromEntries(targetIds.map((id) => [id, text])),
    }));
    toast({
      title: `Findings copied to ${targetIds.length} note${targetIds.length === 1 ? "" : "s"}`,
      status: "success",
      duration: 3000,
    });
  };

  // Reading the note itself while deciding what to rule on it -- the summary in the
  // panel is the classifier's sentence, not what the caregiver wrote.
  const [openNoteId, setOpenNoteId] = React.useState<ListItem["id"] | null>(null);

  // The signature step, revealed only when the server refuses the signing for want of
  // one.
  //
  // Discovered from the failure rather than checked when the modal opens: the check
  // costs an S3 round trip, almost every reviewer already has a signature, and the
  // server refuses the signing either way -- so a pre-check would buy a slower open and
  // a window between the check and the signing in which the answer could change.
  const [needsSignature, setNeedsSignature] = React.useState(false);
  const [signatureData, setSignatureData] = React.useState<string | null>(null);

  const toggleDisposition = (noteId: string, value: Disposition) =>
    setDispositions((prev) => {
      const current = prev[noteId] ?? [];

      return {
        ...prev,
        [noteId]: current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value],
      };
    });

  const flaggedNotes = props.notes.filter(
    (note) => note.priority === "HIGH" || note.priority === "URGENT"
  );

  // Only a note flagged as potentially concerning carries findings, written in its ruling
  // panel. The server refuses findings for any other note.
  const hasFindings = (note: ListItem) => (findings[String(note.id)] ?? "").trim() !== "";

  const renderFindingsEditor = (note: ListItem) => {
    const text = findings[String(note.id)] ?? "";

    return (
      <>
        <Textarea
          maxLength={FINDINGS_MAX_LENGTH}
          placeholder="Findings, Evidence, and Recommendations"
          rows={4}
          value={text}
          onChange={(event) =>
            setFindings((prev) => ({
              ...prev,
              [String(note.id)]: event.target.value,
            }))
          }
        />
        <Flex align="center" justify="space-between" mt={1}>
          {flaggedNotes.length > 1 && text.trim() !== "" ? (
            <CopyFindingsPopover
              targets={flaggedNotes
                .filter((other) => other.id !== note.id)
                .map((other) => ({
                  id: String(other.id),
                  label: dateFormatter.toDateTime(other.visitStartTime),
                  hasFindings: hasFindings(other),
                }))}
              onApply={(targetIds) => copyFindings(text, targetIds)}
            />
          ) : (
            <span />
          )}
          <Text color="gray.500" fontSize="xs">
            {text.length} / {FINDINGS_MAX_LENGTH}
          </Text>
        </Flex>
      </>
    );
  };

  const allDetermined = flaggedNotes.every((note) => determinations[String(note.id)] !== undefined);

  // What the Case will be opened as: the reviewer's pick, else the classifier's suggestion,
  // else nothing yet. Null means she has to choose.
  const subtypeFor = (note: ListItem): IncidentSubtype | null => {
    const picked = subtypes[String(note.id)];

    return picked !== undefined ? picked : note.suggestedIncidentSubtype;
  };

  // An incident is raised only for a note ruled concerning whose box the reviewer ticked.
  // Ruling it non-concerning afterwards drops the choice without having to untick it.
  const incidentFor = (note: ListItem): boolean =>
    determinations[String(note.id)] === "NON_ROUTINE" && createIncidents[String(note.id)] === true;

  // Every incident needs a subtype, because it is what the Salesforce Case is opened as, and
  // at least one disposition. A note without an incident asks neither.
  const allSubtypesChosen = flaggedNotes.every(
    (note) => !incidentFor(note) || subtypeFor(note) !== null
  );
  const allDispositionsChosen = flaggedNotes.every(
    (note) => !incidentFor(note) || (dispositions[String(note.id)] ?? []).length > 0
  );

  const declaration = useQuery({
    ...queries.dailyNotes.attestationDeclaration(),
    enabled: props.isOpen,
  });

  // Whether this member may sign these notes at all: every signature needs a license in force,
  // and an office's sign-off rule can require it to be in its state. Asked when the modal opens,
  // so a block is explained before the reviewer fills anything in rather than at the signature.
  const signOff = useQuery({
    ...queries.dailyNotes.signOffCheck(props.notes.map((note) => note.id)),
    enabled: props.isOpen && props.notes.length > 0,
  });
  const unlicensed = signOff.data ?? [];
  // "GA", or "nursing" where any state's license would do.
  const requiredStates = [
    ...new Set(unlicensed.map((block) => block.requiredLicenseState ?? "nursing")),
  ].join(" / ");

  const approve = useMutation({
    ...mutations.dailyNotes.approve(),
    onSuccess: (response) => {
      // A link rather than jumping to the Attestations tab. Signing a backlog is
      // several batches in a row, and moving the page under someone mid-flow to show
      // them a record they can reach any time is the wrong trade.
      toast({
        title: `${response.approvedCount} daily note${
          response.approvedCount === 1 ? "" : "s"
        } signed`,
        description: (
          <Link
            textDecoration="underline"
            onClick={() => stateService.go("app.visits.dailyNoteAttestations")}
          >
            View attestation
          </Link>
        ),
        status: "success",
        position: "top-right",
        isClosable: true,
        // Long enough to be worth offering the link at all.
        duration: 8000,
      });
      void queryClient.invalidateQueries({ queryKey: ["visitDailyNotes"] });
      props.onSigned();
    },
    onError: (error) => {
      // A refusal for want of a signature is the one failure the reviewer can fix
      // without leaving: reveal the pad instead of reporting a dead end. Everything
      // already entered stays put, which is what makes discovering this late
      // acceptable.
      if (isServerErrorCode(error, "SIGNER_HAS_NO_SIGNATURE")) {
        setNeedsSignature(true);
        toast({
          title: "A signature is needed first",
          description:
            "The attestation prints your signature, and your profile has none on file. " +
            "Draw it below and this batch will be signed — nothing you entered is lost.",
          status: "info",
          position: "top-right",
          isClosable: true,
          duration: 9000,
        });
        return;
      }

      // A license that lapsed or was removed while this was open. The check above is
      // refreshed so the explanation on screen matches the refusal.
      if (isServerErrorCode(error, "SIGNER_NOT_LICENSED")) {
        void signOff.refetch();
        toast({
          title: "Nothing was signed",
          description: "A license is required to sign some of these notes. See the note above.",
          status: "error",
          position: "top-right",
          isClosable: true,
          duration: 9000,
        });
        return;
      }

      // The batch is all-or-nothing, so a failure means nothing was signed. The
      // server's message names why — usually a note replaced by a newer
      // submission while this was open.
      //
      // `formatErrorResponse`, not `error.message`: the raw AxiosError says only
      // "Request failed with status code 409", while the server's body says which
      // note moved.
      toast({
        title: "Nothing was signed",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    },
  });

  const saveSignature = useMutation({
    ...mutations.dailyNotes.saveSignerSignature(),
    onSuccess: () => {
      // Straight back into signing with the identical payload. Making the reviewer
      // press "Sign attestation" a second time would be asking them to confirm
      // something they already confirmed.
      setNeedsSignature(false);
      onSign();
    },
    onError: (error) => {
      toast({
        title: "Could not save the signature",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    },
  });

  // Reset between openings: a ticked box carried over from a previous batch would
  // mean confirming a list nobody looked at.
  React.useEffect(() => {
    if (!props.isOpen) {
      setConfirmed(false);
      setDeterminations({});
      setDispositions({});
      setCreateIncidents({});
      setSubtypes({});
      setFindings({});
      setNeedsSignature(false);
      setSignatureData(null);
    }
  }, [props.isOpen]);

  const signerName = getSignerName();
  const urgentCount = props.notes.filter((note) => note.priority === "URGENT").length;
  const highCount = props.notes.filter((note) => note.priority === "HIGH").length;

  // Why the button is dead, where the reviewer can fix it. A disabled control that says
  // nothing leaves someone hunting for what they missed; the signature is the stronger
  // reason, since it needs a different action than the form above it.
  const blockedReason =
    unlicensed.length > 0
      ? `An active ${requiredStates} license is required to sign ${unlicensed.length} of these notes`
      : needsSignature
      ? "Save your signature above to sign this attestation"
      : !allSubtypesChosen
      ? "Choose an incident subtype for every incident you are creating"
      : !allDispositionsChosen
      ? "Choose at least one disposition for every incident you are creating"
      : null;

  const onSign = () => {
    if (declaration.data === undefined) {
      return;
    }

    approve.mutate({
      visitInstanceDailyNoteIds: props.notes.map((note) => note.id),
      determinations: flaggedNotes.map((note) => ({
        visitInstanceDailyNoteId: note.id,
        determination: determinations[String(note.id)],
        // Only an incident shows the disposition pickers, so a choice left behind by unticking
        // the box is not sent.
        dispositions: incidentFor(note) ? dispositions[String(note.id)] ?? [] : [],
        createIncident: incidentFor(note),
        // Sent only when an incident is being created; the server refuses it otherwise.
        incidentSubtype: incidentFor(note) ? subtypeFor(note) : null,
      })),
      // Only the flagged notes something was written for; the server treats a missing one as
      // none and refuses findings on any other note.
      findings: flaggedNotes.flatMap((note) => {
        const text = (findings[String(note.id)] ?? "").trim();

        return text === "" ? [] : [{ visitInstanceDailyNoteId: note.id, text }];
      }),
      declarationVersion: declaration.data.version,
      confirmed: true,
    });
  };

  return (
    <Modal isOpen={props.isOpen} size="5xl" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>RN Attestation</ModalHeader>

        <ModalBody>
          <Flex direction="column" gap={5}>
            <Flex gap={4}>
              <SummaryBox accent="gray" label="Daily notes" value={props.notes.length} />
              <SummaryBox accent="red" label="Urgent" value={urgentCount} />
              <SummaryBox accent="orange" label="High" value={highCount} />
            </Flex>

            {flaggedNotes.length === 0 ? null : (
              <Box borderColor="orange.200" borderRadius="md" borderWidth={1} p={3}>
                <Text fontSize="md" fontWeight="semibold" mb={1}>
                  Rule on {flaggedNotes.length} concerning note
                  {flaggedNotes.length === 1 ? "" : "s"}
                </Text>
                <Text color="gray.600" fontSize="sm" mb={3}>
                  Confirm whether each note is concerning. A Salesforce incident is only created if
                  you choose to.
                </Text>

                {/* One panel per note, collapsed when there is more than one. Eight
                    disposition chips and a ruling per note stacked flat is unreadable
                    past the second note, and the reviewer only works on one at a time.
                    The header carries the state, so nothing that still needs an answer
                    is hidden by being collapsed. */}
                <Accordion
                  allowMultiple={true}
                  defaultIndex={flaggedNotes.length === 1 ? [0] : []}
                  reduceMotion={true}
                >
                  {flaggedNotes.map((note) => {
                    const ruling = determinations[String(note.id)];
                    const chosen = dispositions[String(note.id)] ?? [];

                    return (
                      <CollapsibleSection
                        key={String(note.id)}
                        header={
                          <Flex align="center" gap={2} wrap="wrap">
                            <Text fontSize="md" fontWeight="semibold">
                              {note.patientName}
                            </Text>
                            <Text color="gray.600" fontSize="sm">
                              {dateFormatter.toDateTime(note.visitStartTime)}
                            </Text>

                            {ruling === undefined ? (
                              <Tag colorScheme="orange" size="md">
                                Needs a ruling
                              </Tag>
                            ) : (
                              <Tag
                                colorScheme={ruling === "NON_ROUTINE" ? "red" : "blue"}
                                size="md"
                              >
                                {ruling === "NON_ROUTINE" ? "Concerning" : "Non-concerning"}
                              </Tag>
                            )}

                            {incidentFor(note) ? (
                              <Tag colorScheme="red" size="md" variant="outline">
                                Incident
                              </Tag>
                            ) : null}

                            {incidentFor(note) && subtypeFor(note) === null ? (
                              <Tag colorScheme="orange" size="md">
                                Needs a subtype
                              </Tag>
                            ) : null}

                            {incidentFor(note) && chosen.length === 0 ? (
                              <Tag colorScheme="orange" size="md">
                                Needs a disposition
                              </Tag>
                            ) : null}

                            {!incidentFor(note) || chosen.length === 0 ? null : (
                              <Tag colorScheme="gray" size="md">
                                {chosen.length} action{chosen.length === 1 ? "" : "s"}
                              </Tag>
                            )}

                            {hasFindings(note) ? (
                              <Tag colorScheme="blue" size="md">
                                Written
                              </Tag>
                            ) : null}
                          </Flex>
                        }
                      >
                        <Flex align="center" gap={2} mb={3} wrap="wrap">
                          <Text color="gray.700" fontSize="md">
                            {note.primaryReason ?? note.summary ?? ""}
                          </Text>
                          <Button size="sm" variant="link" onClick={() => setOpenNoteId(note.id)}>
                            View note
                          </Button>
                        </Flex>

                        <Text color="gray.600" fontSize="sm" mb={1}>
                          Ruling
                        </Text>
                        <Flex gap={2} mb={4} wrap="wrap">
                          <Button
                            borderRadius="full"
                            colorScheme={ruling === "NON_ROUTINE" ? "red" : "gray"}
                            fontWeight="normal"
                            size="sm"
                            variant={ruling === "NON_ROUTINE" ? "solid" : "outline"}
                            onClick={() =>
                              setDeterminations((prev) => ({
                                ...prev,
                                [String(note.id)]: "NON_ROUTINE",
                              }))
                            }
                          >
                            Concerning
                          </Button>
                          <Button
                            borderRadius="full"
                            colorScheme={ruling === "ROUTINE" ? "blue" : "gray"}
                            fontWeight="normal"
                            size="sm"
                            variant={ruling === "ROUTINE" ? "solid" : "outline"}
                            onClick={() =>
                              setDeterminations((prev) => ({
                                ...prev,
                                [String(note.id)]: "ROUTINE",
                              }))
                            }
                          >
                            Non-concerning
                          </Button>
                        </Flex>

                        {/* An incident is opt-in and only offered on a concerning ruling. Once
                            ticked it is asked what kind (pre-selected from the classifier so the
                            usual case is a glance, but still a choice the reviewer owns) and what
                            was done about it. */}
                        {ruling !== "NON_ROUTINE" ? null : (
                          <Box mb={4}>
                            <Checkbox
                              isChecked={createIncidents[String(note.id)] === true}
                              onChange={(event) =>
                                setCreateIncidents((prev) => ({
                                  ...prev,
                                  [String(note.id)]: event.target.checked,
                                }))
                              }
                            >
                              <Text fontSize="md">Create incident in Salesforce</Text>
                            </Checkbox>
                          </Box>
                        )}

                        {!incidentFor(note) ? null : (
                          <Box mb={4}>
                            <Text color="gray.600" fontSize="sm" mb={1}>
                              Incident subtype
                            </Text>
                            <Select
                              maxWidth="420px"
                              placeholder="Choose a subtype"
                              size="sm"
                              value={subtypeFor(note) ?? ""}
                              onChange={(event) =>
                                setSubtypes((prev) => ({
                                  ...prev,
                                  [String(note.id)]:
                                    event.target.value === ""
                                      ? null
                                      : (event.target.value as IncidentSubtype),
                                }))
                              }
                            >
                              {INCIDENT_SUBTYPES.map((subtype) => (
                                <option key={subtype} value={subtype}>
                                  {subtype}
                                </option>
                              ))}
                            </Select>
                            <Text color="gray.500" fontSize="xs" mt={1}>
                              {note.suggestedIncidentSubtype === null
                                ? "The classifier had no suggestion. This is what the Salesforce Case is opened as."
                                : `Suggested by the classifier: ${note.suggestedIncidentSubtype}. This is what the Salesforce Case is opened as.`}
                            </Text>
                          </Box>
                        )}

                        {/* Disposition belongs to the incident: it is asked only once the
                            reviewer has chosen to create one. */}
                        {!incidentFor(note) ? null : (
                          <>
                            <Text color="gray.600" fontSize="sm" mb={1}>
                              Disposition (choose at least one)
                            </Text>
                            <Flex gap={2} wrap="wrap">
                              {DISPOSITIONS.map((option) => {
                                const selected = chosen.includes(option.value);

                                return (
                                  <Button
                                    key={option.value}
                                    borderRadius="full"
                                    colorScheme={selected ? "red" : "gray"}
                                    fontWeight="normal"
                                    size="sm"
                                    variant={selected ? "solid" : "outline"}
                                    onClick={() => toggleDisposition(String(note.id), option.value)}
                                  >
                                    {option.label}
                                  </Button>
                                );
                              })}
                            </Flex>
                          </>
                        )}

                        <Box mt={4}>
                          <Text color="gray.600" fontSize="sm" mb={1}>
                            Findings, Evidence, and Recommendations (optional)
                          </Text>
                          {renderFindingsEditor(note)}
                        </Box>
                      </CollapsibleSection>
                    );
                  })}
                </Accordion>
              </Box>
            )}

            {/* Context rather than work: the notes needing a decision are above, and
                this is the full list the signature will cover. Collapsed so it does not
                push the declaration off the screen. */}
            <Accordion allowToggle={true} reduceMotion={true}>
              <CollapsibleSection
                header={
                  <Text fontSize="md" fontWeight="semibold">
                    All {props.notes.length} note{props.notes.length === 1 ? "" : "s"} in this
                    attestation
                  </Text>
                }
              >
                <Box maxHeight="280px" overflowY="auto">
                  <Table size="md" variant="simple">
                    <Thead position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Patient</Th>
                        <Th>Visit date</Th>
                        <Th>Summary</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {props.notes.map((note) => (
                        <Tr
                          key={String(note.id)}
                          _hover={{ bg: "gray.50" }}
                          cursor="pointer"
                          onClick={() => setOpenNoteId(note.id)}
                        >
                          <Td>{note.patientName}</Td>
                          <Td whiteSpace="nowrap">
                            {dateFormatter.toDateTime(note.visitStartTime)}
                          </Td>
                          <Td>
                            <Text fontSize="md">{note.summary ?? "—"}</Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </CollapsibleSection>
            </Accordion>

            {/* The one block the reviewer cannot clear from here: it needs an admin to add
                a license. Said before the declaration so nobody reads a whole form first. */}
            {unlicensed.length === 0 ? null : (
              <Box bg="red.50" borderColor="red.200" borderRadius="md" borderWidth={1} p={4}>
                <Text fontSize="md" fontWeight="semibold" mb={1}>
                  A {requiredStates} license is required
                </Text>
                <Text color="gray.700" fontSize="md">
                  Signing {unlicensed.length === props.notes.length ? "these" : unlicensed.length}{" "}
                  notes needs an active {requiredStates} license, and your profile has none in
                  force. Ask an admin to add your license under Daily Notes → Licenses.
                </Text>
              </Box>
            )}

            <Box bg="gray.50" borderRadius="md" p={4}>
              {declaration.data === undefined ? (
                <Spinner size="sm" />
              ) : (
                <Text fontSize="md" whiteSpace="pre-wrap">
                  {declaration.data.text}
                </Text>
              )}
            </Box>

            {/* Sits with identity rather than at the top of the modal: it is part of
                who is signing, and it is directly above the button it blocks. */}
            {needsSignature && (
              <Box borderColor="orange.300" borderRadius="md" borderWidth={1} p={4}>
                <Flex align="center" gap={2} mb={2}>
                  <Text fontSize="md" fontWeight="semibold">
                    Your signature
                  </Text>
                  <Tag colorScheme="orange" size="sm">
                    Needs a signature
                  </Tag>
                </Flex>

                <Text color="gray.600" fontSize="md" mb={3}>
                  This attestation prints your signature, and your profile has none saved. Draw it
                  once here — it is kept on your profile, so this is the only time you will be
                  asked.
                </Text>

                <SignatureCanvas
                  isDisabled={saveSignature.isPending}
                  value={signatureData}
                  onChange={setSignatureData}
                />

                <Flex align="center" gap={3} mt={3}>
                  <Button
                    colorScheme="blue"
                    isDisabled={signatureData === null}
                    isLoading={saveSignature.isPending}
                    onClick={() => {
                      if (signatureData === null) {
                        return;
                      }

                      saveSignature.mutate({ signatureData });
                    }}
                  >
                    Save signature and sign
                  </Button>

                  {signatureData === null && (
                    <Text color="gray.500" fontSize="sm">
                      Draw your signature above to continue.
                    </Text>
                  )}
                </Flex>
              </Box>
            )}

            <Flex direction="column" gap={1}>
              <Text color="gray.600" fontSize="md">
                Signing as
              </Text>
              <Text fontWeight="semibold">{signerName}</Text>
            </Flex>

            <Checkbox
              isChecked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            >
              <Text fontSize="md">
                I confirm my identity and intent to sign. This attestation is recorded against every
                daily note listed above.
              </Text>
            </Checkbox>
          </Flex>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          {/* Disabled with the reason stated, per the same control that opens this
              modal: a dead button that says nothing leaves someone hunting for what
              they missed. `shouldWrapChildren` because a disabled button emits no
              pointer events of its own. */}
          <Tooltip
            shouldWrapChildren
            isDisabled={blockedReason === null}
            label={blockedReason ?? ""}
          >
            <Button
              colorScheme="blue"
              isDisabled={
                !confirmed ||
                !allDetermined ||
                !allSubtypesChosen ||
                !allDispositionsChosen ||
                declaration.data === undefined ||
                props.notes.length === 0 ||
                needsSignature ||
                unlicensed.length > 0
              }
              isLoading={approve.isPending}
              onClick={onSign}
            >
              Sign attestation
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>

      {/* Inside the modal so it layers above it: the reviewer is mid-decision and
          should come back to the same half-filled form, not lose it. */}
      <DailyNoteDetailsDrawer
        visitInstanceDailyNoteId={openNoteId}
        onClose={() => setOpenNoteId(null)}
      />
    </Modal>
  );
}

/**
 * The signed-in agency member's name, for the "Signing as" line.
 *
 * The server records the signer from the authenticated connection regardless of
 * what is shown here, so this is a label rather than an input.
 */
function getSignerName(): string {
  const member = auth.getAuthOrFail().data.agencyMember;

  return `${member.firstName} ${member.lastName}`;
}
